import DetailSkeleton from "./DetailSkeleton"
import EmptyDetail from "./EmptyDetail"
import FeedbackSummaryCard from "./FeedbackSummaryCard"
import LogDetailHeader from "./LogDetailHeader"
import MatchedPhysicians from "./MatchedPhysicians"
import PatientLine from "./PatientLine"
import ReportVideosCard from "./ReportVideosCard"
import ScanEngagementCard from "./ScanEngagementCard"
import TagChipGroup from "./TagChipGroup"

/*
  Everything known about one record, scrolling in its own pane.

  `log` is fetched by id on its own (useVideoLogRow), independent of the
  sidebar's paginated, filtered list — so a record far down a large list, or
  outside the sidebar's current filters entirely, still opens in one request
  rather than depending on that record having been paged into view.

  That independence means `log` is no longer synchronously available the moment
  something is selected — it has its own loading and not-found states, which
  `selectedId` (the raw id, separate from whether it resolved to anything) is
  what tells the three apart: nothing selected, selected but not back yet, and
  selected but the fetch came back empty.

  The full empty state is reserved for a record with genuinely nothing behind it.
  A record that matched no videos but did come back with pathologies and
  physicians still shows them — that detail is exactly what explains why nothing
  matched, so hiding it behind a tidier empty state would cost more than it
  saves. The videos card carries its own "no report videos" line in that case.
*/
const LogDetailPane = ({
  selectedId,
  log,
  videos,
  detail,
  isRowLoading,
  isDetailLoading,
  scrollRef,
}) => {
  const hasNothingToShow = !detail && videos.length === 0
  const isLoading = isRowLoading || (!!log && isDetailLoading)
  const notFound = !!selectedId && !isRowLoading && !log

  return (
    <section
      aria-busy={isLoading}
      className="flex h-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-300 bg-white"
    >
      {log && (
        <>
          <LogDetailHeader log={log} videos={videos} />

          {/* Announces the change without reading the whole pane aloud on every
              arrow key. */}
          <p aria-live="polite" className="sr-only">
            {log.AccessionNo} selected
          </p>
        </>
      )}

      {/* relative: this pane's header and live region each carry a sr-only span.
          sr-only is position:absolute with no inset values, and nothing between
          here and <body> is positioned (Navbar is `fixed`, but on a sibling
          subtree, not an ancestor of this pane) — so without a local containing
          block those spans lay out against the document root instead of this
          scrollport, inflating the whole PAGE's scroll height by however far
          the list scrolls internally. `relative` gives them one, with no
          visible effect. */}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto">
        {!selectedId ? (
          <EmptyDetail variant="no-selection" />
        ) : notFound ? (
          <EmptyDetail variant="not-found" />
        ) : isLoading ? (
          <DetailSkeleton />
        ) : (
          <div className="flex flex-col gap-4 p-5">
            {/* Always above the empty state — an errored record is the one
                  most worth explaining. */}
            {log.IsError && (
              <div className="rounded-md bg-red-100 p-3 text-sm text-red-800">
                Error:{" "}
                {log.ErrorDescription ||
                  "An error occurred while processing this video log."}
              </div>
            )}

            {hasNothingToShow ? (
              <EmptyDetail log={log} variant="no-videos" />
            ) : (
              <>
                <PatientLine detail={detail} log={log} videos={videos} />

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
                  <div className="flex min-w-0 flex-col gap-3">
                    <TagChipGroup
                      label="Pathologies"
                      tone="indigo"
                      values={detail?.Pathologies}
                    />
                    <TagChipGroup
                      label="Indications"
                      tone="amber"
                      values={detail?.Indications}
                    />
                    <MatchedPhysicians detail={detail} />
                  </div>

                  <div className="flex min-w-0 flex-col gap-3">
                    <ReportVideosCard
                      uuid={log.ReportVideosUuid}
                      videos={videos}
                    />
                    {log.ReportVideosUuid && (
                      <ScanEngagementCard uuid={log.ReportVideosUuid} />
                    )}
                  </div>
                </div>

                {log.ReportVideosUuid && (
                  <FeedbackSummaryCard uuid={log.ReportVideosUuid} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default LogDetailPane
